import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase, isSupabaseConfigured } from "../supabaseClient";
import { checkText } from "../moderation";
import { hasAcceptedPostingTerms, acceptPostingTerms } from "../postingTerms";

// Declared locally rather than imported from App.js: this page is imported BY
// App.js, so reaching back into it would create an import cycle.
const SITE_URL = "https://betweenthenews.com";

// The Complaint Line: readers put coverage in front of us and we answer it.
//
// This is the part of the product that makes plain what Between the News is —
// a complaints desk for news coverage, not a place that carries the news. It
// is also the app's clearest piece of native participation: submitting,
// backing and tracking a case is something a reader does, not something they
// browse.
//
// Storage note: submissions live in the `comments` table with a NULL
// analysis_id, which is how reader suggestions were already stored. Backing is
// per-device (localStorage) because the table has no counter column — we show
// what YOU backed rather than inventing a global tally we cannot compute.

const BACKED_KEY = "btn-backed-cases";
const NAME_KEY = "btn-reader-name";

function readBacked() {
  try {
    const raw = localStorage.getItem(BACKED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function writeBacked(list) {
  try {
    localStorage.setItem(BACKED_KEY, JSON.stringify(list));
  } catch (e) {}
}

function readName() {
  try {
    return localStorage.getItem(NAME_KEY) || "";
  } catch (e) {
    return "";
  }
}

// Submissions are stored as one text blob; keep a marker so the outlet and the
// reader's objection can be shown apart from each other when rendering.
const SEP = " ||| ";

function parseCase(text) {
  const raw = String(text || "");
  const i = raw.indexOf(SEP);
  if (i === -1) return { subject: raw, objection: "" };
  return { subject: raw.slice(0, i), objection: raw.slice(i + SEP.length) };
}

function timeAgoShort(iso) {
  const then = new Date(iso).getTime();
  if (!then) return "";
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function ComplaintLine() {
  const [subject, setSubject] = useState("");
  const [objection, setObjection] = useState("");
  const [name, setName] = useState(readName);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(hasAcceptedPostingTerms);
  const [cases, setCases] = useState([]);
  const [backed, setBacked] = useState(readBacked);

  const loadCases = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    const { data } = await supabase
      .from("comments")
      .select("id, author, text, created_at")
      .is("analysis_id", null)
      .order("created_at", { ascending: false })
      .limit(40);
    if (data) setCases(data);
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const submit = async () => {
    if (status === "sending") return;
    if (!termsAccepted) {
      setError("Please agree to the posting rules first.");
      return;
    }
    const subjectVerdict = checkText(subject);
    if (!subjectVerdict.ok) {
      setError(subjectVerdict.reason);
      return;
    }
    if (objection.trim()) {
      const objectionVerdict = checkText(objection);
      if (!objectionVerdict.ok) {
        setError(objectionVerdict.reason);
        return;
      }
    }
    setError("");
    setStatus("sending");

    const displayName = name.trim().slice(0, 40) || "A reader";
    try {
      localStorage.setItem(NAME_KEY, displayName);
    } catch (e) {}

    const text = objection.trim()
      ? `${subject.trim()}${SEP}${objection.trim()}`
      : subject.trim();

    const { error: err } = await supabase
      .from("comments")
      .insert({ analysis_id: null, author: displayName, text });

    if (err) {
      setStatus("error");
      return;
    }
    setSubject("");
    setObjection("");
    setStatus("done");
    loadCases();
  };

  const toggleBack = (id) => {
    const next = backed.includes(id) ? backed.filter((x) => x !== id) : [...backed, id];
    setBacked(next);
    writeBacked(next);
  };

  return (
    <div className="complaint-page">
      <title>Complaint Line — Between the News</title>
      <meta
        name="description"
        content="Send us coverage that deserves scrutiny. Readers put headlines in front of our editors and we answer them with a published critique."
      />
      <link rel="canonical" href={`${SITE_URL}/complaint-line/`} />

      <div className="complaint-hero">
        <div className="complaint-kicker">Readers' complaint line</div>
        <h1 className="complaint-title">Put a headline in front of us</h1>
        <p className="complaint-sub">
          We are not a news service and we do not carry anyone's reporting. We are a
          complaints desk for coverage. If a headline misled you, buried the point, or
          quietly took a side, file it here — our editors read every case, and the ones
          that reveal a pattern become a published critique.
        </p>
      </div>

      <div className="complaint-form">
        <label className="complaint-field-label" htmlFor="cl-subject">
          The headline or link that bothered you
        </label>
        <input
          id="cl-subject"
          className="complaint-input"
          placeholder="e.g. BBC: '37 dead after strike'"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={300}
        />

        <label className="complaint-field-label" htmlFor="cl-objection">
          What bothered you about it? <span className="complaint-optional">(optional)</span>
        </label>
        <textarea
          id="cl-objection"
          className="complaint-textarea"
          placeholder="The passive voice does the work here — nobody struck anyone."
          value={objection}
          onChange={(e) => setObjection(e.target.value)}
          rows={3}
          maxLength={800}
        />

        <label className="complaint-field-label" htmlFor="cl-name">
          Sign it as <span className="complaint-optional">(optional)</span>
        </label>
        <input
          id="cl-name"
          className="complaint-input"
          placeholder="A reader"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
        />

        {!termsAccepted && (
          <label className="comment-terms-consent">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                if (e.target.checked) {
                  acceptPostingTerms();
                  setTermsAccepted(true);
                  setError("");
                }
              }}
            />
            <span>
              I agree to the <Link to="/terms">Terms of Use</Link>. There is no tolerance
              for objectionable content or abusive users.
            </span>
          </label>
        )}

        {error && <div className="comment-error">{error}</div>}
        {status === "error" && (
          <div className="comment-error">Something went wrong — try again.</div>
        )}
        {status === "done" && (
          <div className="complaint-done">
            ✓ Filed. Your case is in the queue below — we read every one.
          </div>
        )}

        <button
          className="complaint-submit"
          onClick={submit}
          disabled={status === "sending" || !termsAccepted || !subject.trim()}
        >
          {status === "sending" ? "Filing…" : "File this case"}
        </button>

        <p className="complaint-note">
          Submissions are screened before they appear. We quote headlines only to
          criticise them; we never republish anyone's article.
        </p>
      </div>

      <div className="complaint-queue-header">
        <h2>The queue</h2>
        <span>{cases.length} open case{cases.length === 1 ? "" : "s"}</span>
      </div>

      {cases.length === 0 ? (
        <div className="complaint-empty">
          <p>No cases yet. Be the first to put a headline in front of our editors.</p>
        </div>
      ) : (
        <div className="complaint-list">
          {cases.map((c) => {
            const { subject: s, objection: o } = parseCase(c.text);
            const isBacked = backed.includes(c.id);
            return (
              <div className="complaint-case" key={c.id}>
                <div className="complaint-case-subject">{s}</div>
                {o && <div className="complaint-case-objection">“{o}”</div>}
                <div className="complaint-case-foot">
                  <span className="complaint-case-meta">
                    {c.author} · {timeAgoShort(c.created_at)}
                  </span>
                  <button
                    className={`complaint-back-btn ${isBacked ? "backed" : ""}`}
                    onClick={() => toggleBack(c.id)}
                  >
                    {isBacked ? "✓ You backed this" : "Back this case"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ComplaintLine;
